import React, { useCallback } from 'react'
import { useFormikContext } from 'formik'
import _ from 'lodash'
import VisualTagsField from './VisualTagsField'
import { useStore } from 'app/store'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PERMISSIONS } from 'permissions'
import FormTags from '../FormTags/FormTags'
import { PermissionsGate } from 'app/containers'
import { DATASET_VISUAL_TAGS_LABELS } from 'app/constants/datasets.constants'

const useStyles = makeStyles((theme) =>
  createStyles({
    heading: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 700,
    },
    advancedOptions: {
      margin: '0px !important',
      boxShadow: 'none !important',
    },
  })
)

const DatasetVisualTagsForm = () => {
  const classes = useStyles()
  const { values, setFieldValue } = useFormikContext()
  const {
    dashboard: { permissions },
  } = useStore()
  const dbType = 'visualTags.DB type'
  const isDelDb = _.get(values, dbType) === 'DEL'
  const canCreateDelDataset = permissions[PERMISSIONS.DATASET_CREATE_DEL]
  const canCreateReactivityDataset = permissions[PERMISSIONS.DATASET_CREATE_REACTIVITY]
  let allowedDatasetTypes = ['Standard'];

  if (canCreateDelDataset) {
    allowedDatasetTypes.push('DEL');
  }

  if (canCreateReactivityDataset) {
    allowedDatasetTypes.push('Reactivity');
  }


  const DatasetVisualTagsField = useCallback(
    (props) => {
      return (
        <VisualTagsField
          label={DATASET_VISUAL_TAGS_LABELS[props.name] || props.name}
          {...props}
        />
      )
    },
    []
  )

  return (
    <>
      <PermissionsGate scopes={[PERMISSIONS.DATASET_CREATE_FROM_S3_PATH]}>
        <DatasetVisualTagsField
          name={'s3 path'}
          label={'S3 path'}
          tooltip={
            'A path to a compressed (.zip) archive file on an Amazon AWS S3 bucket.  The archive file must contain .csv files.  Subfolders are not supported.  This field is mandatory if no other files are being uploaded.'
          }
        />
      </PermissionsGate>
      {(canCreateDelDataset || canCreateReactivityDataset) && (
        <DatasetVisualTagsField
          name={'DB type'}
          label={'Dataset type'}
          options={allowedDatasetTypes}
          tooltip={
            'DEL for DNA-encoded library data set, Standard for all other data sets.'
          }
        />
      )}

      {isDelDb && (
        <PermissionsGate
          scopes={[PERMISSIONS.DATASET_SELECT_TRAINING_SCHEME]}
        >
          <DatasetVisualTagsField
            name={'Training Scheme'}
            label={'Training Scheme'}
            tooltip={'Applicable if using X-Chem DEL data. Please refer to user guide.'}
            options={['Mix+Folds', 'Mix-only', 'Folds-only']}
            autoCompleteProps={{
              disableClearable: true,
            }}
          /></PermissionsGate>
      )}

      <>
        <Accordion defaultExpanded={false} className={classes.advancedOptions}>
          <AccordionSummary
            style={{ padding: 0 }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography className={classes.heading}>
              Advanced Options
            </Typography>
          </AccordionSummary>

          <AccordionDetails className="flex-column" style={{ padding: 0 }}>
            <DatasetVisualTagsField
              name={'Seperator'}
              options={['Tab', 'Comma', 'Space', 'Bar']}
              tooltip={'The type of separator used in the datafile.'}
            />
            <DatasetVisualTagsField
              name={'Remove Duplicates'}
              tooltip={'Remove Duplicates Yes/No'}
              options={['No', 'Yes']}
              autoCompleteProps={{
                disableClearable: true,
              }}
            />
            <DatasetVisualTagsField
              name={'Smiles column'}
              tooltip={'The name of the columns contains the smiles data.'}
            />

            <DatasetVisualTagsField
              name={'IDColumn'}
              tooltip={
                'The name of the column contains the unique id of the records.'
              }
            />

            {isDelDb && (
              <>
                <DatasetVisualTagsField
                  name={'Fuzzy labeling'}
                  options={['No', 'Yes']}
                  tooltip={
                    'Use the DEL enrichment thresholds as indicators for a machine learning driven threshold determination.'
                  }
                  autoCompleteProps={{
                    disableClearable: true,
                  }}
                />
                  <PermissionsGate
                  scopes={[PERMISSIONS.DATASET_DEL_THRESHOLD_BUILDER]}
                  >
                <DatasetVisualTagsField
                  name={'TARGET_ENR_threshold'}
                  tooltip={
                    'The threshold used to distinguish between the positive and non binder negative examples.'
                  }
                />
                <DatasetVisualTagsField
                  name={'TARGET_RATIO_threshold'}
                  tooltip={
                    'Please refer to user guide.'
                  }
                />
                <DatasetVisualTagsField
                  name={'NTC_ENR_threshold'}
                  tooltip={
                    'Please refer to user guide.'
                  }
                />
                <DatasetVisualTagsField
                  name={'COMPETITOR_ENR_threshold'}
                  tooltip={
                    'Please refer to user guide.'
                  }
                />
                </PermissionsGate>
                <DatasetVisualTagsField
                  name={'Subsampling'}
                  tooltip={'This option can be computationally expensive. Please refer to user guide.'}
                  options={['Random', 'Cluster-based']}
                  autoCompleteProps={{
                    disableClearable: true,
                  }}
                />
              </>
            )}
            <FormTags
              editable
              values={values}
              setFieldValue={setFieldValue}
              VISUAL_TAGS_LABELS={DATASET_VISUAL_TAGS_LABELS}
            />
          </AccordionDetails>
        </Accordion>
      </>
    </>
  )
}

export default React.memo(DatasetVisualTagsForm)
